import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, ChevronRight, ChevronDown, Calendar, CalendarCheck,
  ShieldCheck, HelpCircle, Compass, Clock, FileText, ClipboardList,
  BookOpen, AlertCircle, CheckCircle2, Milestone, Send, Landmark,
  Globe2, UserCheck,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";

const timelineIcons = [Calendar, CalendarCheck, Clock];
const timelineColors = [
  "bg-teal-700 text-white",
  "bg-sky-600 text-white",
  "bg-violet-600 text-white",
];

const afterStepIcons = [Send, FileText, Landmark, CheckCircle2];
const afterStepColors = [
  "bg-teal-700 text-white",
  "bg-sky-600 text-white",
  "bg-amber-600 text-white",
  "bg-emerald-600 text-white",
];

export default function DVLotteryDatesPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const [openFaq, setOpenFaq] = useState(null);

  const timelineItems = t("dvd.timelineItems", { returnObjects: true });
  const afterSteps = t("dvd.afterSteps", { returnObjects: true });
  const faqs = t("dvd.faqs", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background" data-testid="dv-lottery-dates-page">
      <SEOHead
        title={t("dvd.heroTitle")}
        description={t("dvd.heroSubtitle")}
      />

      {/* HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvd-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-5 font-medium">
            <Calendar className="h-3.5 w-3.5 mr-1.5" />
            {t("dvd.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            {t("dvd.heroTitle")}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            {t("dvd.heroSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvd-hero-elig">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvd.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dvd-hero-start">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <Compass className="mr-2 h-4 w-4" /> {t("dvd.ctaStartGuide")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 1 — REGISTRATION PERIOD */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvd-registration">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvd.regLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvd.regTitle")}</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-5">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvd.regPeriodTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvd.regPeriodDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0">
                    <Globe2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvd.regOfficialTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvd.regOfficialDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvd-reg-cta">
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvd.ctaEligibility")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 2 — DV LOTTERY TIMELINE */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvd-timeline">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvd.tlLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvd.tlTitle")}</h2>
          </div>
          <div className="space-y-0">
            {Array.isArray(timelineItems) && timelineItems.map((item, i) => {
              const Icon = timelineIcons[i] || Calendar;
              return (
                <div key={i} className="flex gap-4 md:gap-6" data-testid={`dvd-timeline-${i}`}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${timelineColors[i] || timelineColors[0]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    {i < timelineItems.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className={i < timelineItems.length - 1 ? "pb-8" : ""}>
                    <Badge variant="secondary" className="mb-2 text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-100">
                      {item.period}
                    </Badge>
                    <h3 className="text-base font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to={lp("/gc/green-card-timeline")} data-testid="dvd-timeline-cta">
              <Button variant="outline" className="font-medium border-slate-200">
                <Clock className="mr-2 h-4 w-4 text-teal-600" /> {t("dvd.ctaTimeline")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHEN RESULTS ARE ANNOUNCED */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvd-results">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvd.resLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvd.resTitle")}</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-5">
            <Card className="border-slate-100 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <CalendarCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvd.resCheckTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvd.resCheckDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200/60 bg-amber-50/30 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{t("dvd.resNoEmailTitle")}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{t("dvd.resNoEmailDesc")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/green-card-lottery")} data-testid="dvd-results-cta">
              <Button variant="outline" className="font-medium border-slate-200">
                <BookOpen className="mr-2 h-4 w-4 text-teal-600" /> {t("dvd.ctaCheckResults")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT HAPPENS AFTER WINNING */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvd-after-winning">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvd.afterLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvd.afterTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvd.afterIntro")}</p>
          </div>
          <div className="space-y-0">
            {Array.isArray(afterSteps) && afterSteps.map((step, i) => {
              const Icon = afterStepIcons[i] || FileText;
              return (
                <div key={i} className="flex gap-4 md:gap-6" data-testid={`dvd-after-step-${i}`}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${afterStepColors[i] || afterStepColors[0]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    {i < afterSteps.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className={i < afterSteps.length - 1 ? "pb-8" : ""}>
                    <h3 className="text-base font-semibold text-slate-900 mb-1">{step.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to={lp("/green-card-lottery")} data-testid="dvd-after-cta">
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium">
                <ArrowRight className="mr-2 h-4 w-4" /> {t("dvd.ctaAfterWinning")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 5 — DV LOTTERY GUIDES (shared nav) */}
      <DVGuidesNav currentPath="/dv-lottery-dates" />

      {/* SECTION 6 — FAQ */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvd-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvd.faqLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvd.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvd-faq-${i}`}>
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvd-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvd-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">{t("dvd.finalTitle")}</h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">{t("dvd.finalSubtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvd-final-elig">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvd.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dvd-final-start">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                {t("dvd.ctaStartGuide")} <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
